import React, { useEffect, useRef, useState } from 'react';
import { US_STATES } from '@/data/usStates';
import { SHIP_TO_LIMITS, normalizeShipTo, validateShipTo } from '@/lib/followupShipTo';

// §patient-address: "Ship to" card on the follow-up checkout. Shows the address
// the follow-up is about to use and lets the patient change it before paying.
// State and the two save paths live in useFollowupShipTo.
//
//   onFile          the address on file, or null if none is known
//   changed         a change that goes out on the submit, or null (→ BE inherits)
//   saved           true once a change was written to the patient profile
//   emptyText       copy when onFile is null
//   addLabel        button label when onFile is null
//   onChange        (address | null) => void | Promise<void | {fieldErrors} | {formError}>;
//                   an error result keeps the form open
//   onEditingChange (bool) => void; the parent holds payment while the form is open
//   validateState   optional (code) => message | '' for flow-specific state gates
const ShipToCard = ({
  onFile, changed, saved,
  emptyText = 'The address on your profile.', addLabel = 'Use a different address',
  onChange, onEditingChange, validateState,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ address: '', city: '', state: '', zip: '' });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const shown = changed || onFile;

  // If the card unmounts with the form open (Back, then Next), release the
  // parent's payment hold; the reopened card starts closed.
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;
  useEffect(() => () => {
    if (onEditingChangeRef.current) onEditingChangeRef.current(false);
  }, []);

  const setEditingBoth = (next) => {
    setEditing(next);
    if (onEditingChange) onEditingChange(next);
  };

  const openForm = () => {
    setDraft({ address: '', city: '', state: '', zip: '', ...(shown || {}) });
    setErrors({});
    setFormError('');
    setEditingBoth(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    const errs = validateShipTo(draft);
    if (!errs.state && validateState) {
      const msg = validateState(normalizeShipTo(draft).state);
      if (msg) errs.state = msg;
    }
    setErrors(errs);
    setFormError('');
    if (Object.keys(errs).length) return;
    setSaving(true);
    let result;
    try {
      result = await onChange(normalizeShipTo(draft));
    } finally {
      if (mounted.current) setSaving(false);
    }
    if (!mounted.current) return;
    if (result && (result.fieldErrors || result.formError)) {
      setErrors(result.fieldErrors || {});
      setFormError(result.formError || '');
      return;
    }
    setEditingBoth(false);
  };

  const field = (key) => ({
    value: draft[key],
    onChange: (e) => setDraft((d) => ({ ...d, [key]: e.target.value })),
    className: `w-full rounded-lg border px-3 py-2 text-sm ${errors[key] ? 'border-red-400' : 'border-gray-300'}`,
    'data-testid': `ship-to-${key}`,
  });
  const err = (key) =>
    errors[key] ? <p className="mt-1 text-xs text-red-600">{errors[key]}</p> : null;

  if (editing) {
    return (
      <form onSubmit={save} noValidate className="mb-4 rounded-xl border border-gray-200 p-4" data-testid="ship-to-form">
        <h4 className="font-semibold text-gray-900 mb-3">Ship to</h4>
        {formError ? (
          <p className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="ship-to-error">
            {formError}
          </p>
        ) : null}
        <div className="space-y-3">
          <div>
            <input type="text" placeholder="Street address, apt/unit" maxLength={SHIP_TO_LIMITS.address} autoComplete="street-address" {...field('address')} />
            {err('address')}
          </div>
          <div>
            <input type="text" placeholder="City" maxLength={SHIP_TO_LIMITS.city} autoComplete="address-level2" {...field('city')} />
            {err('city')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <select autoComplete="address-level1" {...field('state')}>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {err('state')}
            </div>
            <div>
              <input type="text" placeholder="ZIP" inputMode="numeric" maxLength={10} autoComplete="postal-code" {...field('zip')} />
              {err('zip')}
            </div>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="submit" disabled={saving} className="rounded-lg bg-[#2d8a5f] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="ship-to-save">
            {saving ? 'Saving…' : 'Save address'}
          </button>
          <button type="button" disabled={saving} onClick={() => setEditingBoth(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 disabled:opacity-50">
            Cancel
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4" data-testid="ship-to-card">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-gray-900">
          <h4 className="font-semibold mb-1">Ship to</h4>
          {shown ? (
            <>
              <p>{shown.address}</p>
              <p>{[shown.city, [shown.state, shown.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
            </>
          ) : (
            // The lookup may simply not carry the street address, so don't claim
            // there is none on file; the BE still inherits the one it has.
            <p className="text-gray-600">{emptyText}</p>
          )}
        </div>
        <button type="button" onClick={openForm} className="text-sm font-semibold text-[#2d8a5f] underline" data-testid="ship-to-change">
          {shown ? 'Change' : addLabel}
        </button>
      </div>
      {changed ? (
        <p className="mt-2 text-xs text-gray-500">
          We'll send this address to the pharmacy with this order.{' '}
          <button type="button" onClick={() => onChange(null)} className="underline" data-testid="ship-to-undo">
            Undo
          </button>
        </p>
      ) : saved ? (
        <p className="mt-2 text-xs text-gray-500" data-testid="ship-to-saved">
          Saved. This is now your shipping address on file.
        </p>
      ) : null}
    </div>
  );
};

export default ShipToCard;
